'use client';

import { stegaClean } from '@sanity/client/stega';

import { CTAWrapper } from '@/components/global/utils/CTAWrapper';
import { resolveI18nText } from '@/sanity/globals/I18n';
import type { GROQButton } from '@/sanity/lib/definitions';
import { cn } from '@/lib/utils';

export default function ChevronCta({
  data,
  variant = 'dark',
  wide = false,
  language,
  className,
}: {
  data?: GROQButton | null;
  variant?: 'dark' | 'white';
  wide?: boolean;
  language?: string;
  className?: string;
}) {
  if (!data) return null;
  const cta = stegaClean(data)?.cta;
  const label = cta ? resolveI18nText(cta.title, cta.i18n, stegaClean(language)) : 'Please select a CTA';
  if (!label) return null;

  const dark = variant === 'dark';

  const button = (
    <span
      className={cn(
        'flex relative justify-center items-center w-full rounded-[1.375rem] gap-[0.75rem] pl-[5rem] py-[2.25rem] transition-transform duration-300 hover:-translate-y-[0.25rem]',
        wide ? 'pr-[4.25rem]' : 'pr-[5rem]',
        dark ? 'bg-[#145745] shadow-[inset_0_0_3.5125rem_#4CC9A7]' : 'bg-white',
      )}
    >
      <span
        className={cn(
          'min-w-[16.75rem] capitalize text-center font-geist font-[600] text-[2.25rem] tracking-[-0.045rem] max-lg:leading-[3.125rem] lg:leading-[1.347] lg:[text-box-trim:trim-both] lg:[text-box-edge:cap_alphabetic]',
          dark ? 'text-white' : 'text-black',
        )}
      >
        {label}
      </span>
      <span className={cn('flex relative items-center shrink-0', wide ? 'w-[5.75rem]' : 'w-[4.75rem]')}>
        {[0.2, 0.6, 1].map((opacity, i) => (
          <svg
            key={opacity}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            className={cn('w-[3rem] h-[3rem] shrink-0', i < 2 && 'mr-[-1.625rem]')}
          >
            <path
              d="M16.5 12L9.00002 19.5L7.95002 18.45L14.4 12L7.95002 5.55L9.00002 4.5L16.5 12Z"
              fill={dark ? 'white' : 'black'}
              fillOpacity={opacity}
            />
          </svg>
        ))}
      </span>
    </span>
  );

  return cta ? (
    <CTAWrapper cta={cta} className={cn('w-full lg:!w-auto', className)}>
      {button}
    </CTAWrapper>
  ) : (
    <div className={cn('w-full lg:!w-auto', className)}>{button}</div>
  );
}
